/**
 * Mur des fractions — logique pure (Boîte à outils, 6e → 3e).
 *
 * Fractions n/d en entiers (d ≥ 1, le mur va jusqu'aux douzièmes) :
 * égalités, comparaison, fraction d'une quantité, quotient, somme et
 * différence, produit, division et inverse. Chaque calcul s'explique en
 * étapes { tex, say } : la ligne LaTeX et la phrase qui la dit.
 */

export interface Fr {
  n: number;
  d: number;
}

export interface Step {
  tex: string;
  say: string;
}

export type Rel = '<' | '=' | '>';

/** dernier dénominateur du mur */
export const WALL_MAX = 12;

export function gcd(a: number, b: number): number {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a;
}

export const lcm = (a: number, b: number) => (a / gcd(a, b)) * b;

/** forme irréductible, dénominateur positif ; 0 → 0/1 */
export function simplify(f: Fr): Fr {
  if (f.n === 0) return { n: 0, d: 1 };
  const g = gcd(f.n, f.d);
  const s = f.d < 0 ? -1 : 1;
  return { n: (s * f.n) / g, d: (s * f.d) / g };
}

export const cmpFr = (a: Fr, b: Fr): -1 | 0 | 1 => Math.sign(a.n * b.d - b.n * a.d) as -1 | 0 | 1;
export const sameValue = (a: Fr, b: Fr) => cmpFr(a, b) === 0;
export const relOf = (a: Fr, b: Fr): Rel => (['<', '=', '>'] as const)[cmpFr(a, b) + 1]!;

const REL_WORDS: Record<Rel, string> = { '<': 'inférieur à', '=': 'égal à', '>': 'supérieur à' };
const MINUS = '−';

/** n/d en LaTeX : entier si d = 1, « -\frac{…}{…} » si négatif */
export function frTex(f: Fr): string {
  const s = simplifySign(f);
  if (s.d === 1) return String(s.n);
  return `${s.n < 0 ? '-' : ''}\\frac{${Math.abs(s.n)}}{${s.d}}`;
}

/** sans simplifier : « 4/6 » reste 4/6 ; seul le signe passe au numérateur */
function simplifySign(f: Fr): Fr {
  return f.d < 0 ? { n: -f.n, d: -f.d } : f;
}

export function frPlain(f: Fr): string {
  const s = simplifySign(f);
  const body = s.d === 1 ? String(Math.abs(s.n)) : `${Math.abs(s.n)}/${s.d}`;
  return `${s.n < 0 ? MINUS : ''}${body}`;
}

/** bouts de la feuille : « 3 ÷ 4 », « − 2 ÷ 7 », « 5 » */
export function frAtoms(f: Fr): string[] {
  const s = simplifySign(f);
  const body = s.d === 1 ? [String(Math.abs(s.n))] : [String(Math.abs(s.n)), '÷', String(s.d)];
  return s.n < 0 ? [MINUS, ...body] : body;
}

const PART_NAMES: Record<number, readonly [string, string]> = {
  2: ['demi', 'demis'],
  3: ['tiers', 'tiers'],
  4: ['quart', 'quarts'],
  5: ['cinquième', 'cinquièmes'],
  6: ['sixième', 'sixièmes'],
  7: ['septième', 'septièmes'],
  8: ['huitième', 'huitièmes'],
  9: ['neuvième', 'neuvièmes'],
  10: ['dixième', 'dixièmes'],
  11: ['onzième', 'onzièmes'],
  12: ['douzième', 'douzièmes'],
};

/** « 2 tiers », « un quart », « 7 sur 15 » : lecture pour le lecteur d'écran */
export function frWords(f: Fr): string {
  const s = simplifySign(f);
  const sign = s.n < 0 ? 'moins ' : '';
  const n = Math.abs(s.n);
  if (s.d === 1) return `${sign}${n}`;
  const name = PART_NAMES[s.d];
  if (!name) return `${sign}${n} sur ${s.d}`;
  if (n === 1) return `${sign}un ${name[0]}`;
  return `${sign}${n} ${n === 0 ? name[0] : name[1]}`;
}

/** nom d'une part : « tiers », « douzièmes » */
export function partName(d: number, plural = true): string {
  const name = PART_NAMES[d];
  if (!name) return d === 1 ? (plural ? 'unités' : 'unité') : `${d}e${plural ? 's' : ''}`;
  return name[plural ? 1 : 0];
}

/** écriture décimale EXACTE (« 0,75 ») ou null si elle ne s'arrête pas */
export function exactDecimal(f: Fr): string | null {
  const s = simplify(f);
  let rest = s.d;
  let k = 0;
  let twos = 0;
  let fives = 0;
  while (rest % 2 === 0) {
    rest /= 2;
    twos++;
  }
  while (rest % 5 === 0) {
    rest /= 5;
    fives++;
  }
  if (rest !== 1) return null;
  k = Math.max(twos, fives);
  const scaled = Math.round((Math.abs(s.n) * 10 ** k) / s.d);
  const digits = String(scaled).padStart(k + 1, '0');
  const int = digits.slice(0, digits.length - k);
  const dec = digits.slice(digits.length - k);
  return `${s.n < 0 ? MINUS : ''}${int}${k ? `,${dec}` : ''}`;
}

/** troncature à `digits` décimales suivie de « … » (1/3 → « 0,333… ») */
export function truncatedDecimal(f: Fr, digits = 3): string {
  const s = simplify(f);
  const scaled = Math.floor((Math.abs(s.n) * 10 ** digits) / s.d);
  const str = String(scaled).padStart(digits + 1, '0');
  return `${s.n < 0 ? MINUS : ''}${str.slice(0, -digits)},${str.slice(-digits)}…`;
}

/** décimal en LaTeX : « 0{,}75 », « -1{,}5 » */
export const decTex = (s: string) =>
  s.replace(MINUS, '-').replace(',', '{,}').replace('…', '\\ldots');

/** les fractions du mur (d ≤ max) égales à f, du plus petit dénominateur au plus grand */
export function wallEquivalents(f: Fr, max = WALL_MAX): Fr[] {
  const out: Fr[] = [];
  for (let d = 1; d <= max; d++) if ((f.n * d) % f.d === 0) out.push({ n: (f.n * d) / f.d, d });
  return out;
}

/** f écrite sur le dénominateur D (multiple de f.d) : ligne « 2/3 = 8/12 » */
function scaleStep(f: Fr, D: number): Step | null {
  const k = D / f.d;
  if (k === 1) return null;
  return {
    tex: `${frTex(f)} = \\frac{${f.n} \\times ${k}}{${f.d} \\times ${k}} = \\frac{${f.n * k}}{${D}}`,
    say: `On multiplie le numérateur et le dénominateur de ${frPlain(f)} par ${k}.`,
  };
}

/** « un tiers », « une unité » */
const onePart = (d: number) => (d === 1 ? 'une unité' : `un ${partName(d, false)}`);

/**
 * comparer deux fractions positives, en disant pourquoi. `rank` : 6e = 1,
 * 5e = 2 ; avant la 4e, un dénominateur commun qui n'est ni l'un ni l'autre
 * des deux dénominateurs se LIT sur le mur (jusqu'aux douzièmes) au lieu de
 * se calculer.
 */
export function compareSteps(a: Fr, b: Fr, rank = 3): { rel: Rel; steps: Step[] } {
  const rel = relOf(a, b);
  const final = `${frTex(a)} ${rel} ${frTex(b)}`;
  const one = (say: string, tex = final) => ({ rel, steps: [{ tex, say }] });
  if (a.n === 0 || b.n === 0) {
    if (a.n === 0 && b.n === 0)
      return one('Zéro part, quelle que soit leur taille : les deux valent 0.');
    const zero = a.n === 0 ? a : b;
    return one(`Zéro part : ${frPlain(zero)} vaut 0, plus petit que toute fraction non nulle.`);
  }
  if (a.d === b.d) {
    return one(
      `Même dénominateur : on compare les numérateurs, ${a.n} est ${REL_WORDS[rel]} ${b.n}.`,
    );
  }
  if (a.n === b.n) {
    const [small, big] = a.d > b.d ? [a, b] : [b, a];
    return one(
      `Même numérateur : plus on partage, plus les parts sont petites. ${onePart(small.d).replace(/^u/, 'U')} est plus petit qu’${onePart(big.d)}, donc ${frPlain(small)} < ${frPlain(big)}.`,
    );
  }
  if (rel === '=') {
    // l'un est un agrandissement de l'autre, ou les deux se simplifient pareil
    if (b.d % a.d === 0 && b.n === (a.n * b.d) / a.d) {
      const s = scaleStep(a, b.d)!;
      return one(`${s.say} Les deux fractions sont égales.`, s.tex);
    }
    if (a.d % b.d === 0 && a.n === (b.n * a.d) / b.d) {
      const s = scaleStep(b, a.d)!;
      return one(`${s.say} Les deux fractions sont égales.`, s.tex);
    }
    const s = simplify(a);
    return one(
      `Les deux se simplifient en ${frPlain(s)} : elles sont égales.`,
      `${frTex(a)} = ${frTex(s)} \\quad\\text{et}\\quad ${frTex(b)} = ${frTex(s)}`,
    );
  }
  const D = lcm(a.d, b.d);
  const multiple = D === Math.max(a.d, b.d);
  if (!multiple && rank <= 2 && D > WALL_MAX) {
    const [small, big] = rel === '<' ? [a, b] : [b, a];
    return one(
      `Sur le mur, la barre de ${frPlain(big)} est plus longue que celle de ${frPlain(small)}. Le prouver par le calcul (dénominateur commun ${D}) se fait en 4e.`,
    );
  }
  const steps: Step[] = [];
  const sa = scaleStep(a, D);
  const sb = scaleStep(b, D);
  const intro = multiple
    ? `${D} est un multiple de ${Math.min(a.d, b.d)} : on écrit les deux fractions sur ${D}.`
    : rank <= 2
      ? `Sur le mur, les deux tombent juste sur la ligne des ${partName(D)} : on les écrit sur ${D}.`
      : `On cherche un dénominateur commun : ${D}, multiple de ${a.d} et de ${b.d}.`;
  if (sa) steps.push(sa);
  if (sb) steps.push(sb);
  steps[0] = { tex: steps[0]!.tex, say: `${intro} ${steps[0]!.say}` };
  const A = (a.n * D) / a.d;
  const B = (b.n * D) / b.d;
  steps.push({
    tex: `\\frac{${A}}{${D}} ${rel} \\frac{${B}}{${D}} \\quad\\text{donc}\\quad ${final}`,
    say: `Même dénominateur ${D} : ${A} est ${REL_WORDS[rel]} ${B}.`,
  });
  return { rel, steps };
}

/** « une fraction est un quotient » : n/d = n ÷ d, et d × n/d = n */
export function quotientSteps(f: Fr): Step[] {
  const dec = exactDecimal(f);
  const value = dec
    ? `= ${decTex(dec)}`
    : `\\approx ${decTex(truncatedDecimal(f).replace('…', ''))}\\ldots`;
  return [
    {
      tex: `${frTex(f)} = ${f.n} \\div ${f.d} ${value}`,
      say: dec
        ? `Une fraction est un quotient : ${f.n} divisé par ${f.d}.`
        : `Une fraction est un quotient : ${f.n} divisé par ${f.d}, une division qui ne s’arrête pas. ${frPlain(f)} est la valeur exacte.`,
    },
    {
      tex: `${f.d} \\times ${frTex(f)} = ${f.n}`,
      say: `C’est le nombre qui, multiplié par ${f.d}, donne ${f.n}.`,
    },
  ];
}

/** résultat exact d'un calcul : entier, décimal exact ou fraction irréductible */
export function valueTex(f: Fr): string {
  const s = simplify(f);
  if (s.d === 1) return String(s.n);
  const dec = exactDecimal(s);
  return dec ? `${frTex(s)} = ${decTex(dec)}` : frTex(s);
}

/** prendre n/d d'une quantité q (q entier ≥ 1) */
export function ofQuantitySteps(f: Fr, q: number): { result: Fr; part: Fr; steps: Step[] } {
  const result = simplify({ n: f.n * q, d: f.d });
  const part = simplify({ n: q, d: f.d });
  const steps: Step[] = [];
  if (q % f.d === 0) {
    const p = q / f.d;
    steps.push({
      tex: `${q} \\div ${f.d} = ${p}`,
      say: `On partage ${q} en ${f.d} parts égales : chaque part vaut ${p}.`,
    });
    steps.push({
      tex: `${f.n} \\times ${p} = ${f.n * p}`,
      say:
        f.n === 1
          ? `On prend une part : ${p}.`
          : `On prend ${f.n} parts : ${f.n} fois ${p}, soit ${f.n * p}.`,
    });
  } else if (!exactDecimal(part) && (f.n * q) % f.d === 0) {
    steps.push({
      tex: `${f.n} \\times ${q} = ${f.n * q}`,
      say: `${q} ÷ ${f.d} ne tombe pas sur un nombre décimal : on commence par multiplier par ${f.n}.`,
    });
    steps.push({
      tex: `${f.n * q} \\div ${f.d} = ${(f.n * q) / f.d}`,
      say: `Puis on divise par ${f.d}.`,
    });
  } else {
    const dec = exactDecimal(part);
    steps.push({
      tex: `${q} \\div ${f.d} = ${frTex(part)}${dec ? ` = ${decTex(dec)}` : ''}`,
      say: `On partage ${q} en ${f.d} parts égales : chaque part vaut ${frPlain(part)}${dec ? `, soit ${dec}` : ''}.`,
    });
    if (f.n > 1) {
      // la part comme elle vient d'être écrite : 2,5 (décimal exact) ou 10/3
      const resultDec = exactDecimal(result);
      steps.push(
        dec && resultDec
          ? {
              tex: `${f.n} \\times ${decTex(dec)} = ${decTex(resultDec)}`,
              say: `On prend ${f.n} parts : ${f.n} fois ${dec}, soit ${resultDec}.`,
            }
          : {
              tex: `${f.n} \\times ${frTex(part)} = \\frac{${f.n * part.n}}{${part.d}}${simplify({ n: f.n * part.n, d: part.d }).d !== part.d ? ` = ${frTex(result)}` : ''}`,
              say: `On prend ${f.n} parts.`,
            },
      );
    }
  }
  steps.push({
    tex: `\\frac{${f.n}}{${f.d}} \\text{ de } ${q} = \\frac{${f.n}}{${f.d}} \\times ${q} = ${valueTex(result)}`,
    say: 'Prendre une fraction d’une quantité, c’est multiplier cette quantité par la fraction.',
  });
  return { result, part, steps };
}

const reduceStep = (r: Fr): Step | null => {
  if (r.n === 0)
    return r.d === 1 ? null : { tex: `${frTex(r)} = 0`, say: 'Zéro part : le résultat vaut 0.' };
  const s = simplify(r);
  if (s.d === r.d) return null;
  const g = r.d / s.d;
  return { tex: `${frTex(r)} = ${frTex(s)}`, say: `On simplifie par ${g}.` };
};

/** a ± b, avec le dénominateur commun le plus petit */
export function addSteps(a: Fr, b: Fr, op: '+' | '-'): { result: Fr; steps: Step[] } {
  const sign = op === '+' ? 1 : -1;
  const verb = op === '+' ? 'additionne' : 'soustrait';
  const opTex = op === '+' ? '+' : '-';
  const steps: Step[] = [];
  const D = lcm(a.d, b.d);
  const A = (a.n * D) / a.d;
  const B = (b.n * D) / b.d;
  const raw: Fr = { n: A + sign * B, d: D };
  if (a.d === b.d) {
    steps.push({
      tex: `${frTex(a)} ${opTex} ${frTex(b)} = \\frac{${a.n} ${opTex} ${b.n}}{${D}} = ${frTex(raw)}`,
      say: `Même dénominateur : on ${verb} les numérateurs (des ${partName(D)} ${op === '+' ? 'plus' : 'moins'} des ${partName(D)}), le dénominateur ne change pas.`,
    });
  } else {
    const sa = scaleStep(a, D);
    const sb = scaleStep(b, D);
    const intro =
      D === Math.max(a.d, b.d)
        ? `On ne peut ${op === '+' ? 'additionner' : 'soustraire'} que des parts de même taille : ${D} est un multiple de ${Math.min(a.d, b.d)}.`
        : `On ne peut ${op === '+' ? 'additionner' : 'soustraire'} que des parts de même taille : on écrit tout en ${partName(D)} (${D} est un multiple de ${a.d} et de ${b.d}).`;
    const conv = [sa, sb].filter((s): s is Step => s !== null);
    conv[0] = { tex: conv[0]!.tex, say: `${intro} ${conv[0]!.say}` };
    steps.push(...conv);
    steps.push({
      tex: `\\frac{${A}}{${D}} ${opTex} \\frac{${B}}{${D}} = \\frac{${A} ${opTex} ${B}}{${D}} = ${frTex(raw)}`,
      say: `Même dénominateur ${D} : on ${verb} les numérateurs.`,
    });
  }
  const r = reduceStep(raw);
  if (r) steps.push(r);
  return { result: simplify(raw), steps };
}

/** a × b */
export function mulSteps(a: Fr, b: Fr): { result: Fr; steps: Step[] } {
  const raw: Fr = { n: a.n * b.n, d: a.d * b.d };
  const steps: Step[] = [
    {
      tex: `${frTex(a)} \\times ${frTex(b)} = \\frac{${a.n} \\times ${b.n}}{${a.d} \\times ${b.d}} = ${frTex(raw)}`,
      say: 'On multiplie les numérateurs entre eux et les dénominateurs entre eux.',
    },
  ];
  const r = reduceStep(raw);
  if (r) steps.push(r);
  return { result: simplify(raw), steps };
}

/** inverse de f (f ≠ 0) */
export const inverse = (f: Fr): Fr => simplifySign({ n: f.d, d: f.n });

/** a ÷ b (b ≠ 0) : multiplier par l'inverse */
export function divSteps(a: Fr, b: Fr): { result: Fr; steps: Step[] } {
  const inv = inverse(b);
  const m = mulSteps(a, inv);
  return {
    result: m.result,
    steps: [
      {
        tex: `${frTex(b)} \\times ${frTex(inv)} = 1`,
        say: `L’inverse de ${frPlain(b)} est ${frPlain(inv)} : leur produit vaut 1.`,
      },
      {
        tex: `${frTex(a)} \\div ${frTex(b)} = ${frTex(a)} \\times ${frTex(inv)}`,
        say: 'Diviser par un nombre, c’est multiplier par son inverse.',
      },
      ...m.steps,
    ],
  };
}

/** combien de fois b dans a : parts entières et reste (pour la figure du quotient) */
export function piecesOf(a: Fr, b: Fr): { whole: number; rest: Fr } {
  const q = simplify({ n: a.n * b.d, d: a.d * b.n });
  const whole = Math.floor(q.n / q.d);
  return { whole, rest: simplify({ n: q.n - whole * q.d, d: q.d }) };
}

/** ce que montre la figure du quotient, en une phrase (formules entre $ pour MathText) */
export function fitSentence(a: Fr, b: Fr): string {
  const { whole, rest } = piecesOf(a, b);
  const q = simplify({ n: a.n * b.d, d: a.d * b.n });
  const A = `$${frTex(a)}$`;
  const B = `$${frTex(b)}$`;
  const Q = `$${frTex(q)}$`;
  if (a.n === 0) return `${B} ne tient pas du tout dans 0 : le quotient vaut 0.`;
  if (rest.n === 0) return `${B} tient exactement ${whole} fois dans ${A}.`;
  if (whole === 0) return `${B} ne tient pas une fois entière dans ${A} : seulement ${Q} fois.`;
  return `${B} tient ${whole} fois dans ${A}, et il reste encore $${frTex(rest)}$ de ${B} : en tout, ${B} tient ${Q} fois dans ${A}.`;
}

export { REL_WORDS };
